import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';

// === Tickets ===

export function useServiceTickets(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'tickets', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/tickets${params ? `?${params}` : ''}`),
  });
}

export function useServiceTicket(id?: string) {
  return useQuery({
    queryKey: ['service', 'ticket', id],
    queryFn: () => apiFetch<any>(`/api/service/tickets/${id}`),
    enabled: !!id,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service'] }); },
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiFetch<any>(`/api/service/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service'] }); },
  });
}

export function useAssignTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, verantwoordelijkeId }: { id: string; verantwoordelijkeId: string }) =>
      apiFetch<any>(`/api/service/tickets/${id}/toewijzen`, {
        method: 'POST',
        body: JSON.stringify({ verantwoordelijkeId }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service'] }); },
  });
}

// === Notities ===

export function useAddNotitie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tekst, isIntern }: { id: string; tekst: string; isIntern: boolean }) =>
      apiFetch<any>(`/api/service/tickets/${id}/notities`, {
        method: 'POST',
        body: JSON.stringify({ tekst, isIntern }),
      }),
    onSuccess: (_d, vars) => { qc.invalidateQueries({ queryKey: ['service', 'ticket', vars.id] }); },
  });
}

// === Foto's ===

export function useAddFoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, url, beschrijving }: { id: string; url: string; beschrijving?: string }) =>
      apiFetch<any>(`/api/service/tickets/${id}/fotos`, {
        method: 'POST',
        body: JSON.stringify({ url, beschrijving }),
      }),
    onSuccess: (_d, vars) => { qc.invalidateQueries({ queryKey: ['service', 'ticket', vars.id] }); },
  });
}

// === Stats ===

export function useServiceStats() {
  return useQuery({
    queryKey: ['service', 'stats'],
    queryFn: () => apiFetch<any>('/api/service/stats'),
  });
}

// === Dossier tickets ===

export function useDossierTickets(dossierId?: string) {
  return useQuery({
    queryKey: ['service', 'dossier', dossierId, 'tickets'],
    queryFn: () => apiFetch<any[]>(`/api/service/dossier/${dossierId}/tickets`),
    enabled: !!dossierId,
  });
}

// === Garantie ===

export function useDossierGarantie(dossierId?: string) {
  return useQuery({
    queryKey: ['service', 'dossier', dossierId, 'garantie'],
    queryFn: () => apiFetch<any[]>(`/api/service/dossier/${dossierId}/garantie`),
    enabled: !!dossierId,
  });
}

export function useAddGarantie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dossierId, ...data }: any) =>
      apiFetch<any>(`/api/service/dossier/${dossierId}/garantie`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_d, vars) => { qc.invalidateQueries({ queryKey: ['service', 'dossier', vars.dossierId] }); },
  });
}

// === Garantie Registraties ===

export function useGaranties(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'garanties', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/garanties${params ? `?${params}` : ''}`),
  });
}

export function useVerlopendeGaranties(dagen: number = 30) {
  return useQuery({
    queryKey: ['service', 'garanties', 'verlopend', dagen],
    queryFn: () => apiFetch<any[]>(`/api/service/garanties/verlopend?dagen=${dagen}`),
  });
}

export function useDossierGaranties(dossierId?: string) {
  return useQuery({
    queryKey: ['service', 'garanties', 'dossier', dossierId],
    queryFn: () => apiFetch<any[]>(`/api/service/garanties/${dossierId}`),
    enabled: !!dossierId,
  });
}

export function useCreateGarantieRegistratie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/garanties', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'garanties'] }); },
  });
}

export function useUpdateGarantieRegistratie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiFetch<any>(`/api/service/garanties/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'garanties'] }); },
  });
}

// === SLA ===

export function useSlaConfigs() {
  return useQuery({
    queryKey: ['service', 'sla'],
    queryFn: () => apiFetch<any[]>('/api/service/sla'),
  });
}

export function useSlaStatus() {
  return useQuery({
    queryKey: ['service', 'sla', 'status'],
    queryFn: () => apiFetch<any>('/api/service/sla/status'),
  });
}

export function useUpsertSla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/sla', { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'sla'] }); },
  });
}

export function useSeedSlaDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<any>('/api/service/sla/seed-defaults', { method: 'POST' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'sla'] }); },
  });
}

// === Werkorders ===

export function useWerkorders(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'werkorders', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/werkorders${params ? `?${params}` : ''}`),
  });
}

export function useMijnWerkorders() {
  return useQuery({
    queryKey: ['service', 'werkorders', 'mijn'],
    queryFn: () => apiFetch<any[]>('/api/service/werkorders/mijn'),
  });
}

export function useWerkorder(id?: string) {
  return useQuery({
    queryKey: ['service', 'werkorder', id],
    queryFn: () => apiFetch<any>(`/api/service/werkorders/${id}`),
    enabled: !!id,
  });
}

export function useCreateWerkorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/werkorders', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service'] }); },
  });
}

export function useUpdateWerkorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiFetch<any>(`/api/service/werkorders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service'] }); },
  });
}

export function useToggleChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ werkorderId, checkId, afgevinkt }: { werkorderId: string; checkId: string; afgevinkt: boolean }) =>
      apiFetch<any>(`/api/service/werkorders/${werkorderId}/checklist/${checkId}`, {
        method: 'PATCH',
        body: JSON.stringify({ afgevinkt }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'werkorders'] }); },
  });
}

// === Onderdelen ===

export function useOnderdelen(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'onderdelen', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/onderdelen${params ? `?${params}` : ''}`),
  });
}

export function useCreateOnderdeel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/onderdelen', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'onderdelen'] }); },
  });
}

export function useUpdateVoorraad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiFetch<any>(`/api/service/onderdelen/${id}/voorraad`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'onderdelen'] }); },
  });
}

export function useBestellingen(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'bestellingen', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/onderdelen/bestellingen${params ? `?${params}` : ''}`),
  });
}

export function useCreateBestelling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/onderdelen/bestellingen', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'bestellingen'] }); },
  });
}

// === Feedback ===

export function useFeedback() {
  return useQuery({
    queryKey: ['service', 'feedback'],
    queryFn: () => apiFetch<any[]>('/api/service/feedback'),
  });
}

export function useFeedbackStats(maanden?: number) {
  return useQuery({
    queryKey: ['service', 'feedback', 'stats', maanden],
    queryFn: () => apiFetch<any>(`/api/service/feedback/stats${maanden ? `?maanden=${maanden}` : ''}`),
  });
}

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'feedback'] }); },
  });
}

// === Claims ===

export function useClaims(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'claims', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/claims${params ? `?${params}` : ''}`),
  });
}

export function useClaim(id?: string) {
  return useQuery({
    queryKey: ['service', 'claim', id],
    queryFn: () => apiFetch<any>(`/api/service/claims/${id}`),
    enabled: !!id,
  });
}

export function useClaimStats() {
  return useQuery({
    queryKey: ['service', 'claims', 'stats'],
    queryFn: () => apiFetch<any>('/api/service/claims/stats'),
  });
}

export function useCreateClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/claims', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'claims'] }); },
  });
}

export function useUpdateClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiFetch<any>(`/api/service/claims/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'claims'] }); },
  });
}

// === Onderhoud ===

export function useOnderhoudSchemas(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'onderhoud', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/onderhoud${params ? `?${params}` : ''}`),
  });
}

export function useKomendOnderhoud() {
  return useQuery({
    queryKey: ['service', 'onderhoud', 'komend'],
    queryFn: () => apiFetch<any[]>('/api/service/onderhoud/komend'),
  });
}

export function useCreateOnderhoudSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/onderhoud', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'onderhoud'] }); },
  });
}

export function useUpdateBeurt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiFetch<any>(`/api/service/onderhoud/beurten/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'onderhoud'] }); },
  });
}

// === Kennisbank ===

export function useKennisbank(filters?: Record<string, string>) {
  const params = new URLSearchParams(filters || {}).toString();
  return useQuery({
    queryKey: ['service', 'kennisbank', filters],
    queryFn: () => apiFetch<any[]>(`/api/service/kennisbank${params ? `?${params}` : ''}`),
  });
}

export function useKennisbankArtikel(slug?: string) {
  return useQuery({
    queryKey: ['service', 'kennisbank', slug],
    queryFn: () => apiFetch<any>(`/api/service/kennisbank/${slug}`),
    enabled: !!slug,
  });
}

export function useKennisbankCategorieen() {
  return useQuery({
    queryKey: ['service', 'kennisbank', 'categorieen'],
    queryFn: () => apiFetch<string[]>('/api/service/kennisbank/categorieen'),
  });
}

export function useCreateArtikel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/kennisbank', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'kennisbank'] }); },
  });
}

// === Zelfservice ===

export function useZelfserviceTokens(dossierId?: string) {
  const params = dossierId ? `?dossierId=${dossierId}` : '';
  return useQuery({
    queryKey: ['service', 'zelfservice', dossierId],
    queryFn: () => apiFetch<any[]>(`/api/service/zelfservice/tokens${params}`),
  });
}

export function useCreateZelfserviceToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiFetch<any>('/api/service/zelfservice/tokens', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service', 'zelfservice'] }); },
  });
}
